import React, { useEffect, useState } from 'react';
import api from '../services/api';
import '../styles/Vendors.css';

interface Vendor {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  businessName: string;
  city: string;
  businessRegistration?: string;
  gstNumber?: string;
  address?: string;
  totalDrivers: number;
  activeDrivers: number;
  totalJobs: number;
  rating: number;
  isActive: boolean;
  createdAt: string;
}

interface VendorFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  businessName: string;
  city: string;
  businessRegistration: string;
  gstNumber: string;
  address: string;
}

const Vendors: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [formData, setFormData] = useState<VendorFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    businessName: '',
    city: '',
    businessRegistration: '',
    gstNumber: '',
    address: '',
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async (search?: string) => {
    setIsLoading(true);
    try {
      const data = await api.getVendors({ search, limit: 100 });
      setVendors(data.vendors);
      setError('');
    } catch (err: any) {
      setError('Failed to load vendors');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchVendors(searchTerm);
  };

  const handleToggleStatus = async (vendorId: string, currentStatus: boolean) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this vendor?`)) {
      return;
    }

    try {
      await api.toggleVendorStatus(vendorId, !currentStatus);
      fetchVendors(searchTerm);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update vendor status');
    }
  };

  const handleDelete = async (vendorId: string) => {
    if (!window.confirm('Are you sure you want to delete this vendor? This action cannot be undone.')) {
      return;
    }

    try {
      await api.deleteVendor(vendorId);
      fetchVendors(searchTerm);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete vendor');
    }
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      businessName: '',
      city: '',
      businessRegistration: '',
      gstNumber: '',
      address: '',
    });
    setFormError('');
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      businessName: '',
      city: '',
      businessRegistration: '',
      gstNumber: '',
      address: '',
    });
    setFormError('');
  };

  const openEditModal = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      firstName: vendor.firstName,
      lastName: vendor.lastName,
      email: vendor.email || '',
      phone: vendor.phone,
      password: '',
      businessName: vendor.businessName,
      city: vendor.city,
      businessRegistration: vendor.businessRegistration || '',
      gstNumber: vendor.gstNumber || '',
      address: vendor.address || '',
    });
    setShowEditModal(true);
    setFormError('');
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedVendor(null);
    setFormError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (isEdit: boolean): boolean => {
    if (!formData.firstName.trim()) {
      setFormError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setFormError('Last name is required');
      return false;
    }
    if (!formData.phone.trim()) {
      setFormError('Phone number is required');
      return false;
    }
    if (!formData.businessName.trim()) {
      setFormError('Business name is required');
      return false;
    }
    if (!formData.city.trim()) {
      setFormError('City is required');
      return false;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setFormError('Please enter a valid email address');
      return false;
    }
    if (!isEdit && !formData.password) {
      setFormError('Password is required');
      return false;
    }
    if (!isEdit && formData.password && formData.password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const handleCreateVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!validateForm(false)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = { ...formData };
      // Remove empty optional fields
      if (!payload.email) delete payload.email;
      if (!payload.businessRegistration) delete payload.businessRegistration;
      if (!payload.gstNumber) delete payload.gstNumber;
      if (!payload.address) delete payload.address;

      await api.createVendor(payload);
      closeCreateModal();
      fetchVendors(searchTerm);
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Failed to create vendor');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!validateForm(true) || !selectedVendor) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = { ...formData };
      // Remove password if not changed
      if (!payload.password) delete payload.password;
      // Remove empty optional fields
      if (!payload.email) delete payload.email;
      if (!payload.businessRegistration) delete payload.businessRegistration;
      if (!payload.gstNumber) delete payload.gstNumber;
      if (!payload.address) delete payload.address;

      await api.updateVendor(selectedVendor._id, payload);
      closeEditModal();
      fetchVendors(searchTerm);
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Failed to update vendor');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (isLoading && vendors.length === 0) {
    return (
      <div className="vendors-page">
        <div className="loading">Loading vendors...</div>
      </div>
    );
  }

  return (
    <div className="vendors-page">
      <div className="page-header">
        <div>
          <h1>Vendor Management</h1>
          <p>Manage fleet vendors and their accounts</p>
        </div>
        <div className="header-actions">
          <button className="btn-create-vendor" onClick={openCreateModal}>
            ➕ Create New Vendor
          </button>
          <button className="refresh-button" onClick={() => fetchVendors(searchTerm)}>
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="search-bar">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search vendors by name, business, phone, email, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="submit">🔍 Search</button>
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                fetchVendors('');
              }}
              className="clear-search"
            >
              ✕ Clear
            </button>
          )}
        </form>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* Desktop Table */}
      <div className="desktop-table-container">
        <div className="table-container">
          <table className="vendors-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Business Name</th>
                <th>Owner</th>
                <th>Contact</th>
                <th>City</th>
                <th>Fleet</th>
                <th>Jobs</th>
                <th>Rating</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((vendor) => (
                <tr key={vendor._id} className={!vendor.isActive ? 'inactive-row' : ''}>
                  <td>
                    <span className={`status-badge ${vendor.isActive ? 'active' : 'inactive'}`}>
                      {vendor.isActive ? '🟢 Active' : '⚫ Inactive'}
                    </span>
                  </td>
                  <td>
                    <strong>{vendor.businessName}</strong>
                    {vendor.gstNumber && (
                      <div className="secondary-text">GST: {vendor.gstNumber}</div>
                    )}
                  </td>
                  <td>
                    {vendor.firstName} {vendor.lastName}
                  </td>
                  <td>
                    <div>{vendor.phone}</div>
                    {vendor.email && <div className="secondary-text">{vendor.email}</div>}
                  </td>
                  <td>{vendor.city}</td>
                  <td>
                    <div>{vendor.activeDrivers}/{vendor.totalDrivers}</div>
                    <div className="secondary-text">online/total</div>
                  </td>
                  <td>{vendor.totalJobs || 0}</td>
                  <td>⭐ {vendor.rating?.toFixed(1) || '5.0'}</td>
                  <td>{formatDate(vendor.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => openEditModal(vendor)}
                        className="btn-edit"
                        title="Edit vendor"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleToggleStatus(vendor._id, vendor.isActive)}
                        className={vendor.isActive ? 'btn-deactivate-small' : 'btn-activate-small'}
                        title={vendor.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {vendor.isActive ? '🔒' : '🔓'}
                      </button>
                      <button
                        onClick={() => handleDelete(vendor._id)}
                        className="btn-delete"
                        title="Delete vendor"
                        disabled={vendor.totalDrivers > 0}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {vendors.length === 0 && !isLoading && (
            <div className="empty-state">
              <p>{searchTerm ? 'No vendors found matching your search' : 'No vendors found'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-card-list">
        {vendors.map((vendor) => (
          <div key={vendor._id} className="data-card">
            <div className="data-card-header">
              <div>
                <div className="data-card-title">{vendor.businessName}</div>
                <div className="data-card-subtitle">
                  {vendor.firstName} {vendor.lastName}
                </div>
              </div>
              <span className={`status-badge ${vendor.isActive ? 'active' : 'inactive'}`}>
                {vendor.isActive ? '🟢' : '⚫'}
              </span>
            </div>

            <div className="data-card-body">
              <div className="data-card-row">
                <span className="data-card-label">Phone:</span>
                <span className="data-card-value">{vendor.phone}</span>
              </div>
              {vendor.email && (
                <div className="data-card-row">
                  <span className="data-card-label">Email:</span>
                  <span className="data-card-value">{vendor.email}</span>
                </div>
              )}
              <div className="data-card-row">
                <span className="data-card-label">City:</span>
                <span className="data-card-value">{vendor.city}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Fleet:</span>
                <span className="data-card-value">
                  {vendor.activeDrivers}/{vendor.totalDrivers} drivers
                </span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Jobs:</span>
                <span className="data-card-value">{vendor.totalJobs || 0}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Rating:</span>
                <span className="data-card-value">⭐ {vendor.rating?.toFixed(1) || '5.0'}</span>
              </div>
              {vendor.gstNumber && (
                <div className="data-card-row">
                  <span className="data-card-label">GST:</span>
                  <span className="data-card-value">{vendor.gstNumber}</span>
                </div>
              )}
              <div className="data-card-row">
                <span className="data-card-label">Created:</span>
                <span className="data-card-value">{formatDate(vendor.createdAt)}</span>
              </div>
            </div>

            <div className="data-card-actions">
              <button onClick={() => openEditModal(vendor)} className="btn-edit-full">
                ✏️ Edit
              </button>
              <button
                onClick={() => handleToggleStatus(vendor._id, vendor.isActive)}
                className={vendor.isActive ? 'btn-deactivate' : 'btn-activate'}
              >
                {vendor.isActive ? '🔒 Deactivate' : '🔓 Activate'}
              </button>
              <button
                onClick={() => handleDelete(vendor._id)}
                className="btn-delete-full"
                disabled={vendor.totalDrivers > 0}
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}

        {vendors.length === 0 && !isLoading && (
          <div className="empty-state">
            <p>{searchTerm ? 'No vendors found matching your search' : 'No vendors found'}</p>
          </div>
        )}
      </div>

      {/* Create Vendor Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={closeCreateModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Vendor</h2>
              <button onClick={closeCreateModal} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleCreateVendor} className="vendor-form">
              {formError && <div className="form-error">{formError}</div>}

              <h3>Owner Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+91XXXXXXXXXX"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="vendor@example.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Minimum 6 characters"
                  required
                  minLength={6}
                />
              </div>

              <h3>Business Information</h3>
              <div className="form-group">
                <label htmlFor="businessName">Business Name *</label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  placeholder="ABC Towing Services"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Mumbai"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gstNumber">GST Number</label>
                  <input
                    type="text"
                    id="gstNumber"
                    name="gstNumber"
                    value={formData.gstNumber}
                    onChange={handleInputChange}
                    placeholder="27XXXXX1234X1XX"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="businessRegistration">Business Registration No.</label>
                <input
                  type="text"
                  id="businessRegistration"
                  name="businessRegistration"
                  value={formData.businessRegistration}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Business address (optional)"
                  rows={3}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="btn-cancel-form"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit-form"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Vendor Modal */}
      {showEditModal && selectedVendor && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Vendor</h2>
              <button onClick={closeEditModal} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleUpdateVendor} className="vendor-form">
              {formError && <div className="form-error">{formError}</div>}

              <h3>Owner Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-firstName">First Name *</label>
                  <input
                    type="text"
                    id="edit-firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-lastName">Last Name *</label>
                  <input
                    type="text"
                    id="edit-lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="edit-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+91XXXXXXXXXX"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-email">Email Address</label>
                  <input
                    type="email"
                    id="edit-email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="vendor@example.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="edit-password">New Password (leave blank to keep current)</label>
                <input
                  type="password"
                  id="edit-password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Minimum 6 characters"
                  minLength={6}
                />
              </div>

              <h3>Business Information</h3>
              <div className="form-group">
                <label htmlFor="edit-businessName">Business Name *</label>
                <input
                  type="text"
                  id="edit-businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  placeholder="ABC Towing Services"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-city">City *</label>
                  <input
                    type="text"
                    id="edit-city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Mumbai"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-gstNumber">GST Number</label>
                  <input
                    type="text"
                    id="edit-gstNumber"
                    name="gstNumber"
                    value={formData.gstNumber}
                    onChange={handleInputChange}
                    placeholder="27XXXXX1234X1XX"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="edit-businessRegistration">Business Registration No.</label>
                <input
                  type="text"
                  id="edit-businessRegistration"
                  name="businessRegistration"
                  value={formData.businessRegistration}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-address">Address</label>
                <textarea
                  id="edit-address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Business address (optional)"
                  rows={3}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="btn-cancel-form"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit-form"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendors;
